import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface Exchange {
  id: string;
  name: string;
  status: 'healthy' | 'unhealthy' | 'not_configured';
  capabilities: {
    name: string;
    orderTypes: string[];
    features: string[];
    feeStructure: {
      maker: number;
      taker: number;
      withdraw: string;
    };
    limits: {
      minOrderValue: number;
      maxOrderValue: number;
      maxOrders: number;
    };
  };
  lastChecked?: string;
  error?: string;
}

interface ExchangeSelectorProps {
  selectedExchanges: string[];
  onExchangeChange: (exchanges: string[]) => void;
  multiSelect?: boolean;
  className?: string;
  showHealthStatus?: boolean;
  showCapabilities?: boolean;
}

const ExchangeSelector: React.FC<ExchangeSelectorProps> = ({
  selectedExchanges,
  onExchangeChange,
  multiSelect = false,
  className = '',
  showHealthStatus = true,
  showCapabilities = false
}) => {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCapabilities, setExpandedCapabilities] = useState<string | null>(null);

  useEffect(() => {
    fetchExchangeData();
  }, []);

  const fetchExchangeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the centralized API client which handles authentication properly
      const { exchangeApi } = await import('@/services/api');
      
      const [capabilitiesData, healthData] = await Promise.all([
        exchangeApi.getCapabilities(),
        exchangeApi.getHealthCheck()
      ]);

      // exchangeApi methods return ApiResponse<T>, so access the data property
      const capabilitiesResponse = capabilitiesData.data || capabilitiesData;
      const healthResponse = healthData.data || healthData;

      // Combine capabilities and health data
      const exchangeList: Exchange[] = Object.keys(capabilitiesResponse.capabilities || {}).map(exchangeId => ({
        id: exchangeId,
        name: capabilitiesResponse.capabilities[exchangeId].name,
        status: healthResponse.exchanges?.[exchangeId]?.status || 'unknown',
        capabilities: capabilitiesResponse.capabilities[exchangeId],
        lastChecked: healthResponse.exchanges?.[exchangeId]?.lastChecked,
        error: healthResponse.exchanges?.[exchangeId]?.error
      }));

      setExchanges(exchangeList);

    } catch (err) {
      console.error('Error fetching exchange data:', err);
      setError('Failed to load exchange information');
      
      // Fallback to basic exchange data
      setExchanges([
        { id: 'binance', name: 'Binance', status: 'not_configured', capabilities: {} as any },
        { id: 'coinbase', name: 'Coinbase Pro', status: 'not_configured', capabilities: {} as any },
        { id: 'kraken', name: 'Kraken', status: 'not_configured', capabilities: {} as any }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleExchangeToggle = (exchangeId: string) => {
    if (multiSelect) {
      const newSelection = selectedExchanges.includes(exchangeId)
        ? selectedExchanges.filter(id => id !== exchangeId)
        : [...selectedExchanges, exchangeId];
      onExchangeChange(newSelection);
    } else {
      onExchangeChange([exchangeId]);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'unhealthy':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'not_configured':
        return <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'Connected';
      case 'unhealthy':
        return 'Connection Error';
      case 'not_configured':
        return 'Not Configured';
      default:
        return 'Unknown';
    }
  };

  const toggleCapabilities = (exchangeId: string) => {
    setExpandedCapabilities(expandedCapabilities === exchangeId ? null : exchangeId);
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error && exchanges.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-2">Failed to load exchanges</p>
          <button
            onClick={fetchExchangeData}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="space-y-3">
        {exchanges.map(exchange => (
          <div
            key={exchange.id}
            className={`
              border rounded-lg p-4 cursor-pointer transition-all duration-200
              ${selectedExchanges.includes(exchange.id)
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 bg-white'
              }
              ${exchange.status === 'unhealthy' ? 'opacity-75' : ''}
            `}
            onClick={() => handleExchangeToggle(exchange.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <input
                    type={multiSelect ? 'checkbox' : 'radio'}
                    name="exchange"
                    checked={selectedExchanges.includes(exchange.id)}
                    onChange={() => handleExchangeToggle(exchange.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {exchange.name}
                  </p>
                  {showHealthStatus && (
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusIcon(exchange.status)}
                      <span className={`text-xs ${
                        exchange.status === 'healthy' ? 'text-green-600' :
                        exchange.status === 'unhealthy' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {getStatusText(exchange.status)}
                      </span>
                      {exchange.lastChecked && (
                        <span className="text-xs text-gray-500">
                          • {new Date(exchange.lastChecked).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  )}
                  {exchange.error && (
                    <p className="text-xs text-red-600 mt-1 truncate">
                      {exchange.error}
                    </p>
                  )}
                </div>
              </div>

              {showCapabilities && exchange.capabilities.name && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCapabilities(exchange.id);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm ml-2"
                >
                  {expandedCapabilities === exchange.id ? 'Hide Info' : 'Show Info'}
                </button>
              )}
            </div>

            {/* Expanded capabilities */}
            {showCapabilities && expandedCapabilities === exchange.id && exchange.capabilities.name && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Order Types</h4>
                    <div className="flex flex-wrap gap-1">
                      {exchange.capabilities.orderTypes.map(type => (
                        <span key={type} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Features</h4>
                    <div className="flex flex-wrap gap-1">
                      {exchange.capabilities.features.map(feature => (
                        <span key={feature} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Fees</h4>
                    <div className="text-xs text-gray-600">
                      <div>Maker: {(exchange.capabilities.feeStructure.maker * 100).toFixed(2)}%</div>
                      <div>Taker: {(exchange.capabilities.feeStructure.taker * 100).toFixed(2)}%</div>
                      <div>Withdraw: {exchange.capabilities.feeStructure.withdraw}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Limits</h4>
                    <div className="text-xs text-gray-600">
                      <div>Min Order: ${exchange.capabilities.limits.minOrderValue}</div>
                      <div>Max Order: ${exchange.capabilities.limits.maxOrderValue.toLocaleString()}</div>
                      <div>Max Active: {exchange.capabilities.limits.maxOrders}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {exchanges.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          <InformationCircleIcon className="h-12 w-12 mx-auto mb-4" />
          <p>No exchanges available</p>
        </div>
      )}

      {/* Multi-select helper text */}
      {multiSelect && selectedExchanges.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            {selectedExchanges.length} exchange{selectedExchanges.length !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
};

export default ExchangeSelector; 